import { reaction } from "mobx";
import { observer, useLocalObservable } from "mobx-react";
import { Button, Tag, Tooltip, Typography, Row, Col, Divider } from "antd";
import { useNavigate } from "react-router-dom";
import { getCountryFlag } from "../../../../../utils/helpers";

import * as MSF from "../../../../../components/OFTable/filters/MultiselectFilter";
import * as NF from "../../../../../components/OFTable/filters/NumericalFilter";
import * as SF from "../../../../../components/OFTable/filters/StateFilter";
import * as DRF from "../../../../../components/OFTable/filters/DateRangeFilter";

import OFTable from "../../../../../components/OFTable/OFTable";
import { CrudTableButtons } from "../../../_common/CrudTableButtons";

import copy from "copy-to-clipboard";
import _ from "lodash";

import { translateKey as t } from "../../../../../locales/translation";

import { ClientsController } from "../../ClientsController";
import { AppStore } from "~/stores/AppStore";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAdd,
  faArrowsSplitUpAndLeft,
  faBars,
  faCheckCircle,
  faCircleCheck,
  faCircleXmark,
  faCloud,
  faCopy,
  faCrown,
  faEdit,
  faEye,
  faFileArrowUp,
  faFileImport,
  faGear,
  faHome,
  faIdCard,
  faMessage,
  faRetweet,
  faTrash,
  faUser,
  faSave,
  faDownload,
  faClone,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { faGoogle, faWindows } from "@fortawesome/free-brands-svg-icons";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

const ClientsTable = ({ store, controller }: { store: AppStore; controller: ClientsController }) => {
  const navigate = useNavigate();
  const localStore = useLocalObservable(() => ({
    get tableActions() {
      const menus = [
        {
          title: t("lblDataImporter", "Data Importer"),
          action: "dataImporter",
          render: hasPermission("dataImport"),
          icon: <FontAwesomeIcon icon={faFileImport} />,
          func: (row) => {
            dataImporter(row);
          },
          className: "mr-2",
        },
        {
          title: t("lblResetMasterData", "Reset Master Data"),
          action: "resetMasterData",
          render: hasPermission("resetMasterData"),
          icon: <FontAwesomeIcon icon={faRetweet} />,
          func: (row) => {
            resetMasterDataModal(row);
          },
          className: "mr-2",
        },
        {
          title: t("lblDemoDataPost", "Demo Data Post"),
          action: "demoDataPost",
          render: true,
          icon: <FontAwesomeIcon icon={faFileArrowUp} />,
          func: (row) => {
            openDemoDataPostModal(row);
          },
        },
        {
          title: t("lblCloneClient", "Clone Client"),
          action: "cloneClient",
          render: store.user?.roles.includes("ADMIN"),
          icon: <FontAwesomeIcon icon={faClone} />,
          func: (row) => {
            openCloneClientModal(row);
          },
        },
        {
          title: t("lblManageCronjobs", "Manage Cronjobs"),
          action: "manageCronjobs",
          render: store.user?.roles.includes("ADMIN"),
          icon: <FontAwesomeIcon icon={faClock} />,
          func: async (row) => {
            await store.setClient(row.clientRef);
            navigate(`/configuration/clients/${row._id}/cronjobs`, { state: { clientRef: row.clientRef } });
          },
        },
        {
          action: "divider",
          render: true,
        },
        {
          title: t("lblSaveScenarioSnapshot", "Save Scenario Snapshot"),
          action: "saveScenarioSnapshot",
          render: hasPermission("snapshotSave"),
          icon: <FontAwesomeIcon icon={faSave} />,
          func: (row) => {
            openSaveScenarioSnapshotModal(row);
          },
          className: "mr-2",
        },
        {
          title: t("lblLoadScenarioSnapshot", "Load Scenario Snapshot"),
          action: "loadScenarioSnapshot",
          render: hasPermission("snapshotLoad"),
          icon: <FontAwesomeIcon icon={faDownload} />,
          func: (row) => {
            openLoadScenarioSnapshotModal(row);
          },
          className: "mr-2",
        },
        // {
        //   title: t("lblDeleteScenarioSnapshot", "Delete Scenario Snapshot"),
        //   action: "deleteScenarioSnapshot",
        //   render: true,
        //   icon: <FontAwesomeIcon icon={faTrash} />,
        //   func: (row) => {
        //     openDeleteScenarioSnapshotModal(row);
        //   },
        //   className: "mr-2",
        // },

        // {
        //   title: t("lblClientSettings", "Client Settings"),
        //   action: "clientSettings",
        //   render: true,
        //   icon: <FontAwesomeIcon icon={faGear} />,
        //   func: (row) => {
        //     openClientSettingsModal(row);
        //   },
        // },
      ];

      return menus.filter((m) => m.render);
    },
  }));

  const dataImporter = async (row) => {
    await store.setClient(row.clientRef);
    controller.setDataImporterModal(null, {
      visible: true,
      loading: false,
      row: row,
      uploadLoading: false,
      importedData: [],
      file: null,
      postDataErrors: [],
    });
  };

  const resetMasterDataModal = async (row) => {
    await store.setClient(row.clientRef);
    controller.setResetMasterDataModal(null, {
      visible: true,
      loading: false,
      row: row,
    });
  };

  const openDemoDataPostModal = async (row) => {
    await store.setClient(row.clientRef);

    controller.loadDepots();

    controller.setDemoDataPostModal(null, {
      visible: true,
      loading: false,
      row: row,
    });
  };

  const openCloneClientModal = (row) => {
    controller.setCloneClientModal(null, {
      visible: true,
      source: row,
    });
  };

  const openSaveScenarioSnapshotModal = async (row) => {
    try {
      controller.setSaveScenarioSnapshotModal(null, {
        visible: true,
        loading: true,
        clientRow: row,
        spinMessage: "Setting Client",
      });

      await store.setClient(row.clientRef);

      await controller.setSaveScenarioSnapshotModal("spinMessage", "Fetching Depots");
      await controller.loadDepots();

      controller.setSaveScenarioSnapshotModal("loading", false);
      controller.setSaveScenarioSnapshotModal("spinMessage", "");
    } catch (error) {
      controller.setSaveScenarioSnapshotModal(null, {
        visible: false,
        loading: false,
        clientRow: null,
        spinMessage: "",
      });
    }
  };

  const hasPermission = (permissionRef) => {
    return store.user?.permissions.some((p) => p.ref === permissionRef && p.current) || false;
  };

  const openLoadScenarioSnapshotModal = async (row) => {
    try {
      controller.setLoadScenarioSnapshotModal(null, {
        visible: true,
        loading: true,
        clientRow: row,
        spinMessage: "Setting Client",
      });

      await store.setClient(row.clientRef);
      controller.setLoadScenarioSnapshotModal("spinMessage", "Fetching Depots");
      await controller.loadDepots();

      controller.setLoadScenarioSnapshotModal("spinMessage", "Fetching Scenario Snapshots");
      await controller.loadScenarioSnapshots();

      controller.setLoadScenarioSnapshotModal("loading", false);
      controller.setLoadScenarioSnapshotModal("spinMessage", "");
    } catch (error) {
      controller.setLoadScenarioSnapshotModal(null, {
        visible: false,
        loading: false,
        clientRow: null,
        spinMessage: "",
      });
    }
  };

  const openDeleteScenarioSnapshotModal = async () => {
    try {
      controller.setDeleteScenarioSnapshotModal(null, {
        visible: true,
        loading: true,
        spinMessage: "Fetching Scenario Snapshots",
      });

      await controller.loadScenarioSnapshots();

      controller.setDeleteScenarioSnapshotModal("loading", false);
      controller.setDeleteScenarioSnapshotModal("spinMessage", "");
    } catch (error) {
      controller.setDeleteScenarioSnapshotModal(null, {
        visible: false,
        loading: false,
        spinMessage: "",
      });
    }
  };

  const renderActions = (row) => {
    const slotStyle = {
      width: 24,
      height: 24,
      flex: "0 0 24px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    };
    const isOfPro = row?.dbName?.includes("of-pro");
    const isMaster = row?.proOptions?.master;

    const isVisible = (ta: any) => {
      if (!isOfPro || isMaster) return false;
      if (row.disableResetMasterData && (ta.action === "resetMasterData" || ta.action === "demoDataPost")) return false;
      if (ta.action === "manageCronjobs" && !_.get(row, "proOptions.demo", false)) return false;
      return true;
    };

    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        {localStore.tableActions.map((ta: any, i: number) => {
          if (ta.action === "divider") {
            return <Divider key={`d${i}`} type="vertical" style={{ margin: "0 4px", height: 18, flex: "0 0 auto" }} />;
          }

          if (!isVisible(ta)) {
            return <span key={i} style={{ ...slotStyle, visibility: "hidden" }} />;
          }

          return (
            <Tooltip title={ta.title} mouseLeaveDelay={0} key={i}>
              <Button size="small" onClick={() => ta.func(row)} style={slotStyle}>
                {ta.icon}
              </Button>
            </Tooltip>
          );
        })}
      </div>
    );
  };

  const getRowIndex = (row: any) => {
    return controller.clients.findIndex((du) => du._id === row._id) + 1;
  };

  // Contains all the columns of the CRUD, including the additional ones (index, createdAt and updatedAtTimestamp)
  const structureWithAdditionalColumns = () => {
    let columns: Array<any> = [
      // {
      //   Header: t("lblUsers", "Users"),
      //   id: "card",
      //   // disableSortBy: true,
      //   disableFilters: false,
      //   accessor: (row) => row,
      //   Cell: (data, value) => renderClientsInfo(data),
      // },
      {
        Header: controller.isReadOnly ? t("lblView", "View") : t("lblEdit", "Edit"),
        id: "action",
        width: 40,
        disableSortBy: true,
        disableFilters: true,
        accessor: (row) => _.get(row, "_id"),
        Cell: (item) => (
          <Button
            onClick={async () => {
              await store.setClient(item.clientRef);
              controller.openEditClientWizard(item);
            }}
            icon={controller.isReadOnly ? <FontAwesomeIcon icon={faEye} /> : <FontAwesomeIcon icon={faEdit} />}
            size="small"
          />
        ),
        responsiveRender: "lg",
        show: store.user?.roles.includes("ADMIN"),
      },
      {
        Header: t("lblIndex", "Index"),
        id: "index",
        width: 40,
        accessor: (row) => getRowIndex(row),
        Filter: NF.NumericalFilter,
        filterMethod: NF.FilteringMethod,
        Cell: (row) => (
          <Typography.Text disabled style={{ cursor: "pointer" }}>
            {getRowIndex(row)}
          </Typography.Text>
        ),
        responsiveRender: "lg",
      },
      {
        Header: t("lblActive", "Active"),
        width: 50,
        id: "active",
        accessor: (row: any) => _.get(row, "active", false),
        Filter: (props) => (
          <SF.StateFilter
            {...props}
            structure={[
              { title: t("lblActive", "Active"), value: true },
              { title: t("lblInactive", "Inactive"), value: false },
            ]}
          />
        ),
        filterMethod: (value, query) => value === query,
        Cell: (data, value) => (value ? <FontAwesomeIcon icon={faCircleCheck} style={{ color: "#52C41A" }} /> : <FontAwesomeIcon icon={faCircleXmark} style={{ color: "#F5222D" }} />),
        responsiveRender: "lg",
      },
      {
        Header: t("lblClientRef", "ClientRef"),
        id: "clientRef",
        width: 120,
        accessor: (row: any) => _.get(row, "clientRef", ""),
        Cell: (data, value) => value,
        enableGlobalSearch: true,
        responsiveRender: "lg",
      },
      {
        Header: t("lblName", "Name"),
        id: "clientName",
        width: 180,
        accessor: (row: any) => _.get(row, "name", ""),
        Cell: (data, value) => value,
        enableGlobalSearch: true,
        responsiveRender: "lg",
      },
      {
        Header: t("lblPartner", "Partner"),
        id: "partner",
        width: 80,
        accessor: (row: any) => _.get(row, "proOptions.partner.ref", ""),
        Cell: (data, value) => value,
        enableGlobalSearch: true,
        responsiveRender: "lg",
        show: store.user?.roles.includes("ADMIN"),
      },
      {
        Header: t("lblVertical", "Vertical"),
        id: "vertical",
        width: 260,
        style: { textAlign: "center" },
        accessor: (row: any) => _.get(row, "proOptions.vertical", ""),
        Cell: (data, value) => {
          let verticalContent = null;
          if (value) {
            let foundVertical = controller.verticals.find((v) => v.ref === value);
            if (foundVertical) {
              verticalContent = (
                <Tag color={foundVertical.color ?? "blue"} style={{ marginRight: 0 }}>
                  {foundVertical.description.toUpperCase()}
                </Tag>
              );
              // verticalContent = <span style={{ color: foundVertical?.colorCode ? foundVertical.colorCode : null }}>{foundVertical.description}</span>;
            }
          }

          return verticalContent;
        },
        enableGlobalSearch: true,
        responsiveRender: "lg",
      },
      {
        Header: t("lblCountry", "Country"),
        id: "country",
        width: 60,
        accessor: (row: any) => _.get(row, "proOptions.country", ""),
        Cell: (data, value) => {
          if (value) {
            return <img alt={"flag"} style={{ width: 25 }} src={getCountryFlag(value)} />;
          }
        },

        enableGlobalSearch: true,
        responsiveRender: "lg",
      },
      {
        Header: t("lblDatabase", "Database"),
        id: "dbName",
        width: 260,
        accessor: (row: any) => _.get(row, "dbName", ""),
        Cell: (data, value) => value,
        enableGlobalSearch: true,
        responsiveRender: "lg",
        show: hasPermission("showDatabaseColumn"),
      },
      {
        Header: t("lblDemoClient", "Demo"),
        width: 50,
        id: "demo",
        accessor: (row: any) => _.get(row, "proOptions.demo", false),
        Filter: (props) => (
          <SF.StateFilter
            {...props}
            structure={[
              { title: t("lblActive", "Active"), value: true },
              { title: t("lblInactive", "Inactive"), value: false },
            ]}
          />
        ),
        filterMethod: (value, query) => value === query,
        Cell: (data, value) => (value ? <FontAwesomeIcon icon={faCircleCheck} style={{ color: "#52C41A" }} /> : <FontAwesomeIcon icon={faCircleXmark} style={{ color: "#F5222D" }} />),
        responsiveRender: "lg",
      },
      {
        Header: t("lblActions", "Actions"),
        id: "tableActions",
        width: store.user?.roles.includes("DEMO_USER") ? 130 : 280,
        enableGlobalSearch: true,
        accessor: (row) => null,
        Cell: (data, value) => renderActions(data),
        disableSortBy: true,
        disableFilters: true,
        show: true,
      },

      {
        Header: <Tooltip title={`${t("lblCreatedAt", "Created At")} (${t("lblFormat", "Format")}: DD/MM/YYYY HH:mm)`}>{t("lblCreatedAt", "Created At")}</Tooltip>,
        id: "createdAt",
        width: 150,
        accessor: (row) => (_.get(row, "createdAt", "") ? dayjs(_.get(row, "createdAt")).format("DD/MM/YYYY HH:mm") : ""),
        rawValue: (row) => (_.get(row, "createdAt", 0) ? dayjs(_.get(row, "createdAt")).valueOf() : 0),
        excludeZero: true,
        Filter: (props) => <DRF.DateRangeFilter {...props} />,
        filterMethod: (value, query) => DRF.FilteringMethod(value, query, "DD/MM/YYYY HH:mm"),
        Cell: (data, value) => <Typography.Text style={{ cursor: "pointer" }}>{value}</Typography.Text>,
        responsiveRender: "lg",
      },
      {
        Header: <Tooltip title={`${t("lblUpdatedAt", "Updated At")} (${t("lblFormat", "Format")}: DD/MM/YYYY HH:mm)`}>{t("lblUpdatedAt", "Updated At")}</Tooltip>,
        id: "updatedAt",
        width: 150,
        accessor: (row) => (_.get(row, "updatedAt", "") ? dayjs(_.get(row, "updatedAt")).format("DD/MM/YYYY HH:mm") : ""),
        rawValue: (row) => (_.get(row, "updatedAt", 0) ? dayjs(_.get(row, "updatedAt")).valueOf() : 0),
        excludeZero: true,
        Filter: (props) => <DRF.DateRangeFilter {...props} />,
        filterMethod: (value, query) => DRF.FilteringMethod(value, query, "DD/MM/YYYY HH:mm"),
        Cell: (data, value) => <Typography.Text style={{ cursor: "pointer" }}>{value}</Typography.Text>,
        responsiveRender: "lg",
      },
      {
        Header: t("lblUpdatedBy", "Updated By"),
        id: "updatedBy",
        width: 150,
        accessor: (row: any) => _.get(row, "updatedBy", ""),
        Cell: (data, value) => <Typography.Text style={{ cursor: "pointer" }}>{value}</Typography.Text>,
        responsiveRender: "lg",
      },
    ];

    return columns;
  };

  // A lighter structure, which does not contain the additional columns (index, createdAt and updatedAtTimestamp)
  const structureWithoutAdditionalColumns = () => {
    return structureWithAdditionalColumns().filter((item) => item.id !== "index" && item.id !== "createdAt" && item.id !== "updatedAt" && item.id !== "updatedBy");
  };

  const buildTableColumns = () => {
    // Changes the table structure when the "Show/Hide Additional Columns" button is clicked
    reaction(
      () => controller.additionalColumnsAreVisible,
      (additionalColumnsAreVisible) => {
        if (additionalColumnsAreVisible) {
          if (controller?.tableInstance) controller.tableInstance.computeColumns(structureWithAdditionalColumns());
        } else {
          controller.tableInstance.computeColumns(structureWithoutAdditionalColumns());
        }
      }
    );

    // The "light" structure is shown by default, when the CRUD loads
    return structureWithoutAdditionalColumns();
  };

  const getContextMenuObj = () => {
    return {
      title: null,
      tagObj: { title: "username" },
      singleMenus: (row) => [
        {
          name: t("lblCopyToClipboard", "Copy to Clipboard"),
          type: "item",
          icon: <FontAwesomeIcon icon={faCopy} />,
          render: true,
          disabled: controller.tableInstance.multiSelectedRows.totSelected > 1,
          func: () => copyRow(row),
        },
        {
          name: t("lblOther", "Other"),
          type: "subMenu",
          icon: <FontAwesomeIcon icon={faBars} />,
          render: true,
          disabled: controller.tableInstance.multiSelectedRows.totSelected > 1,
          subMenus: [
            {
              name: t("lblCopyUsername", "Copy Username"),
              type: "item",
              icon: <FontAwesomeIcon icon={faCopy} />,
              render: true,
              disabled: false,
              func: () => controller.copyText(row.username),
            },
            {
              name: t("lblCopyEmail", "Copy Email"),
              type: "item",
              icon: <FontAwesomeIcon icon={faCopy} />,
              render: true,
              disabled: false,
              func: () => controller.copyText(row.email),
            },
            {
              name: t("lblCopyFirstName", "Copy First Name"),
              type: "item",
              icon: <FontAwesomeIcon icon={faCopy} />,
              render: true,
              disabled: false,
              func: () => controller.copyText(row.firstName),
            },
            {
              name: t("lblCopyLastName", "Copy Last Name"),
              type: "item",
              icon: <FontAwesomeIcon icon={faCopy} />,
              render: true,
              disabled: false,
              func: () => controller.copyText(row.lastName),
            },
          ],
        },
      ],
    };
  };

  // Copy the row data in a user-friendly text format
  const copyRow = (row) => {
    let copyText: string = "";

    copyText += t("lblUser", "User") + "\n";
    copyText += "● " + t("lblActive", "Active") + ": " + String(row.active) + "\n";
    copyText += "● " + t("lblUsername", "Username") + ": " + row.username + "\n";
    copyText += "● " + t("lblEmail", "Email") + ": " + row.email + "\n";
    copyText += "● " + t("lblFirstName", "First Name") + ": " + row.firstName + "\n";
    copyText += "● " + t("lblLastName", "Last Name") + ": " + row.lastName + "\n";
    copyText += "● " + t("lblProvider", "Provider") + ": " + controller.providers.find((p) => p.ref === row.provider)?.name + "\n";

    copy(copyText);
    store.displayNotification(t("lblRowCopiedToClipboard", "Row Copied to Clipboard"), "info", false);
  };

  //--------------------------------------------------------------------------------------------------------------

  const renderClientsInfo = (user) => {
    let proider;

    switch (_.get(user, "provider", "")) {
      case "azuread":
        proider = (
          <Tag color="cyan" style={{ margin: 0, cursor: "pointer" }}>
            <FontAwesomeIcon icon={faWindows} style={{ marginRight: 5 }} />
            <span>Microsoft</span>
          </Tag>
        );
        break;
      case "google":
        proider = (
          <Tag color="magenta" style={{ margin: 0, cursor: "pointer" }}>
            <FontAwesomeIcon icon={faGoogle} style={{ marginRight: 5 }} />
            <span>Google</span>
          </Tag>
        );
        break;
      case "ldapauth":
        proider = (
          <Tag color="geekblue" style={{ margin: 0, cursor: "pointer" }}>
            <FontAwesomeIcon icon={faArrowsSplitUpAndLeft} style={{ marginRight: 5 }} />
            <span>LDAP</span>
          </Tag>
        );
        break;
      case "local":
        proider = (
          <Tag color="purple" style={{ margin: 0, cursor: "pointer" }}>
            <FontAwesomeIcon icon={faHome} style={{ marginRight: 5 }} />
            <span>{t("lblLocal", "Local")}</span>
          </Tag>
        );
        break;
      case "mlauth":
        proider = (
          <Tag color="purple" style={{ margin: 0, cursor: "pointer" }}>
            <FontAwesomeIcon icon={faCloud} style={{ marginRight: 5 }} />
            <span>{t("lblMlauth", "MLAuth")}</span>
          </Tag>
        );
        break;
      default:
        proider = (
          <Tag style={{ margin: 0, cursor: "pointer" }}>
            <span>{t("lblUnspecified", "Unspecified")}</span>
          </Tag>
        );
        break;
    }

    return (
      <div style={{ textAlign: "left" }}>
        <div style={{ fontWeight: "bold", textAlign: "left", borderBottom: "1px solid #000", marginBottom: 5 }}>
          <Row gutter={16} style={{ padding: 5 }}>
            <Col span={12}>
              <FontAwesomeIcon icon={faUser} style={{ marginRight: 10 }} />
              {_.get(user, "username", "")}
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              {user?.active ? <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#52C41A" }} /> : <FontAwesomeIcon icon={faCircleXmark} style={{ color: "#F5222D" }} />}
            </Col>
          </Row>
        </div>

        <Row gutter={16} style={{ padding: "0px 5px" }}>
          <Col span={12}>
            <div className="truncate">
              <FontAwesomeIcon icon={faIdCard} style={{ marginRight: 5 }} />
              {_.get(user, "firstName", "")} {_.get(user, "lastName", "")}
            </div>
          </Col>

          <Col span={12}>
            <div className="truncate">
              <FontAwesomeIcon icon={faMessage} style={{ marginRight: 5 }} />
              {_.get(user, "email", "")}
            </div>
          </Col>
        </Row>

        <Row gutter={16} style={{ padding: "5px 5px 0px 5px" }}>
          <Col className="gutter-row" span={12}>
            {proider}
          </Col>
          <Col className="gutter-row" span={12}>
            <div className="truncate">
              <FontAwesomeIcon icon={faCrown} style={{ marginRight: 5 }} />
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  const { isReadOnly, crudCommon, openEditForm, apiLoading } = controller;

  // Removes "To edit the cell - double click on it" info
  const tableInfo: Array<any> = crudCommon.renderTableInfo().filter((info) => info.key !== 1);

  //qwerty this needs to be hooked with the oftable
  // console.log(apiLoading);

  return (
    <OFTable
      // loading={apiLoading}
      ndnConfig={null}
      controller={controller}
      columns={buildTableColumns()}
      dataObj="clients"
      exportName={t("lblClients", "Clients")}
      tableInstance={(instance) => controller.setTableInstance(instance)}
      showCheckbox={store.user?.roles.includes("SUPER_ADMIN")}
      editableCells={false} //editableCells={!isReadOnly}
      globalSearch={true}
      tableInfo={tableInfo}
      fixedColumns={4}
      tableCardHeight={85}
      onCardClick={(row) => openEditForm(row)}
      onRowClick={(row) => {
        controller.setSelectedClient(row);
        controller.clickedRow = row;
      }}
      // onMultiSelection={() => crudCommon.handleMultiselection()}
      // multiSelectRowObj="multiSelectKeyPressed"

      shiftKeyFlag="shiftKeyPressed"
      menuButtons={[
        <CrudTableButtons
          controller={controller}
          key={"CrudTableButtons"}
          hideDeleteButton={!store.user?.roles.includes("ADMIN")}
          singleDeleteOnly={true}
          onDelete={() => controller.deleteClients()}
          onCreate={() => controller.setNewClientModal("visible", true)}
          overrideOnCreate={true}
          createButtonTitle={t("lblClient", "Client")}
        />,

        <Button size="small" onClick={() => openDeleteScenarioSnapshotModal()} className="mr-2" style={store.user?.roles.includes("DEMO_USER") && { display: "none" }}>
          {t("lblScenarioSnapshots", "Scenario Snapshots")}
        </Button>,
      ]}
      // contextMenuObj={getContextMenuObj()}
      // contextMenuWidth={200}
    />
  );
};

export default ClientsTable;
